import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UsersIcon, ArrowRightOnRectangleIcon, ArrowPathIcon, BuildingOfficeIcon, QuestionMarkCircleIcon, KeyIcon, CurrencyDollarIcon, MagnifyingGlassIcon, FunnelIcon, PlusIcon, XMarkIcon, XCircleIcon as XCircleIconOutline, UserCircleIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import mspService, { MSPCustomer, MSP } from '../../services/mspService';
import LoadingSpinner from '../LoadingSpinner';

export const MSPDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [msp, setMSP] = useState<MSP | null>(null);
  const [customers, setCustomers] = useState<MSPCustomer[]>([]);
  const [leads, setLeads] = useState<MSPCustomer[]>([]);
  const [stats, setStats] = useState({
    directCustomers: 0,
    inviteCodeCustomers: 0,
    totalCustomers: 0,
    activeOpportunities: 0,
    totalMonthlySpend: 0,
    leadsCount: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [impersonating, setImpersonating] = useState<number | null>(null);
  const [closingCustomer, setClosingCustomer] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterApiKey, setFilterApiKey] = useState<boolean | null>(null);
  const [filterActive, setFilterActive] = useState<boolean | null>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [showNewEnvironmentModal, setShowNewEnvironmentModal] = useState(false);
  const [newEnvironmentForm, setNewEnvironmentForm] = useState({
    jobNumber: '',
    companyName: ''
  });
  const [isCreatingEnvironment, setIsCreatingEnvironment] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({
    contactName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    logo: null as File | null
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    // Initialize MSP auth
    mspService.initializeAuth();
    fetchData();
  }, []);
  
  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filterApiKey, filterActive, currentPage, recordsPerPage]);

  const fetchData = async () => {
    try {
      if (!isSearching) {
        setIsLoading(true);
      }
      setError('');
      
      // Build query parameters
      const params: any = {
        page: currentPage,
        limit: recordsPerPage
      };
      
      if (searchTerm) params.search = searchTerm;
      if (filterApiKey !== null) params.hasActiveCredentials = filterApiKey;
      if (filterActive !== null) params.isActive = filterActive;
      
      const [profileData, customersData] = await Promise.all([
        mspService.getProfile(),
        mspService.getCustomers(params)
      ]);

      setMSP(profileData);
      
      // Separate leads from regular customers
      const allCustomers = customersData.customers || [];
      const leadsList = allCustomers.filter(c => c.inviteCodeUsed && c.inviteCodeUsed !== 'INTERNAL');
      const regularCustomers = allCustomers.filter(c => !c.inviteCodeUsed || c.inviteCodeUsed === 'INTERNAL');
      
      setCustomers(regularCustomers);
      setLeads(leadsList);
      
      // Calculate total monthly spend from customers
      const totalMonthlySpend = (customersData.customers || []).reduce((sum, customer) => {
        return sum + (customer.monthlySpend || 0);
      }, 0);
      
      setStats({
        ...(customersData.stats || {
          totalCustomers: 0,
          directCustomers: 0,
          inviteCodeCustomers: 0,
          activeOpportunities: 0,
          leadsCount: 0
        }),
        activeOpportunities: customersData.stats?.activeOpportunities || 0,
        leadsCount: customersData.stats?.leadsCount || 0,
        totalMonthlySpend
      });
      
      setTotalPages(customersData.totalPages || 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
      if (err.response?.status === 401) {
        navigate('/msp/login');
      }
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  const handleImpersonate = async (customerId: number) => {
    try {
      setImpersonating(customerId);
      const response = await mspService.impersonateCustomer(customerId);
      
      if (response.success) {
        // Store user data for auth context
        const userData = {
          id: response.customer.id,
          email: response.customer.email,
          firstName: response.customer.firstName,
          lastName: response.customer.lastName,
          companyName: response.customer.companyName,
          isActive: response.customer.isActive,
          isVerified: response.customer.isVerified,
          lastLogin: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Navigate to the main dashboard as the impersonated customer
        window.location.href = '/dashboard'; // Force full page reload to reinitialize auth
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to impersonate customer');
      setImpersonating(null);
    }
  };

  const handleCloseOpportunity = async (customerId: number) => {
    try {
      setClosingCustomer(customerId);
      await mspService.updateCustomer(customerId, { isActive: false });
      
      // Refresh the customer list
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to close opportunity');
    } finally {
      setClosingCustomer(null);
    }
  };

  const handleReopenOpportunity = async (customerId: number) => {
    try {
      setClosingCustomer(customerId);
      await mspService.updateCustomer(customerId, { isActive: true });
      
      // Refresh the customer list
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reopen opportunity');
    } finally {
      setClosingCustomer(null);
    }
  };

  const handleCreateEnvironment = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreatingEnvironment(true);
    setError('');

    try {
      const response = await mspService.createQuickCustomer({
        firstName: 'Ref',
        lastName: newEnvironmentForm.jobNumber,
        companyName: newEnvironmentForm.companyName,
        email: '',
        inviteCode: 'INTERNAL'
      });

      // Reset form and close modal
      setNewEnvironmentForm({
        jobNumber: '',
        companyName: ''
      });
      setShowNewEnvironmentModal(false);
      
      // Refresh the customer list
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create environment');
    } finally {
      setIsCreatingEnvironment(false);
    }
  };

  const handleLogout = async () => {
    await mspService.logout();
    navigate('/msp/login');
  };

  const handleOpenProfile = () => {
    if (msp) {
      setProfileForm({
        contactName: msp.contactName || '',
        email: msp.email || '',
        phone: msp.phone || '',
        password: '',
        confirmPassword: '',
        logo: null
      });
      setProfileError('');
      setShowProfileModal(true);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    setProfileError('');

    try {
      // Validate passwords match if provided
      if (profileForm.password && profileForm.password !== profileForm.confirmPassword) {
        setProfileError('Passwords do not match');
        return;
      }

      // Prepare form data
      const formData = new FormData();
      formData.append('contactName', profileForm.contactName);
      formData.append('email', profileForm.email);
      if (profileForm.phone) formData.append('phone', profileForm.phone);
      if (profileForm.password) formData.append('password', profileForm.password);
      if (profileForm.logo) {
        console.log('Appending logo to FormData:', {
          name: profileForm.logo.name,
          type: profileForm.logo.type,
          size: profileForm.logo.size
        });
        formData.append('logo', profileForm.logo);
      }

      // Log FormData contents
      formData.forEach((value, key) => {
        console.log(`FormData ${key}:`, value);
      });

      // Update profile
      console.log('Updating profile...');
      const updatedMSP = await mspService.updateProfile(formData);
      console.log('Profile update response:', updatedMSP);
      setMSP(updatedMSP);
      setShowProfileModal(false);
      
      // Refresh data
      fetchData();
    } catch (err: any) {
      console.error('Profile update error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update profile';
      setProfileError(errorMessage);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              {msp?.logoUrl ? (
                <img 
                  src={msp.logoUrl.startsWith('http') 
                    ? msp.logoUrl 
                    : `${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}${msp.logoUrl}`
                  }
                  alt={`${msp.company} logo`}
                  className="h-8 w-auto max-w-[9rem] object-contain mb-1"
                />
              ) : (
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{msp?.company || 'MSP'}</h2>
              )}
              <p className="text-sm font-bold text-gray-600 dark:text-gray-400">MSP Dashboard</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleOpenProfile}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <UserCircleIcon className="h-4 w-4 mr-2" />
                Profile
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}


        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Active Opportunities
                </dt>
                <div className="group relative">
                  <QuestionMarkCircleIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 cursor-help" />
                  <div className="absolute right-0 w-64 p-2 mt-2 text-sm bg-gray-900 text-white rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    Active customers assigned to your MSP account
                  </div>
                </div>
              </div>
              <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                {stats.activeOpportunities}
              </dd>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Leads
                </dt>
                <div className="group relative">
                  <QuestionMarkCircleIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 cursor-help" />
                  <div className="absolute right-0 w-64 p-2 mt-2 text-sm bg-gray-900 text-white rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    Customers assigned to your MSP with invite code not equal to INTERNAL
                  </div>
                </div>
              </div>
              <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                {stats.leadsCount}
              </dd>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Monthly Revenue
                </dt>
                <div className="group relative">
                  <QuestionMarkCircleIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 cursor-help" />
                  <div className="absolute right-0 w-64 p-2 mt-2 text-sm bg-gray-900 text-white rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    Total monthly AWS spend across all your customers (cached from last sync)
                  </div>
                </div>
              </div>
              <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                ${(Number(stats.totalMonthlySpend) || 0).toFixed(0)}
              </dd>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search Input */}
            <div className="md:w-1/3">
              <label htmlFor="search" className="sr-only">Search customers</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="search"
                  id="search"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Search by name, email, or company"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                    setIsSearching(true);
                  }}
                />
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex items-center space-x-4 md:ml-auto">
              <div className="flex items-center">
                <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
              </div>
              
              {/* API Key Filter */}
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700"
                  checked={filterApiKey === true}
                  onChange={(e) => {
                    setFilterApiKey(e.target.checked ? true : null);
                    setCurrentPage(1);
                    setIsSearching(true);
                  }}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">API Key Connected</span>
              </label>
              
              {/* Active Filter */}
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700"
                  checked={filterActive === true}
                  onChange={(e) => {
                    setFilterActive(e.target.checked ? true : null);
                    setCurrentPage(1);
                    setIsSearching(true);
                  }}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Active Only</span>
              </label>
              
              {/* Clear Filters */}
              {(searchTerm || filterApiKey !== null || filterActive !== null) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterApiKey(null);
                    setFilterActive(null);
                    setCurrentPage(1);
                  }}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                Customer Environments
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowNewEnvironmentModal(true)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  New Environment
                </button>
                <button
                  onClick={fetchData}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-1" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
          
          {/* Compact Table view */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-2/5">
                    Opportunity
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                    API Key
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                    Monthly Spend
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/12">
                    Status
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/5">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No customers found
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-2 whitespace-nowrap w-2/5">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {customer.companyName || customer.email}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">
                            {`${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'No name'}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap w-1/6">
                        <div className="flex items-center">
                          {customer.hasActiveCredentials ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Connected
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                              <XCircleIcon className="h-3 w-3 mr-1" />
                              Not Connected
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap w-1/6">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {customer.monthlySpend !== undefined && customer.monthlySpend > 0 ? 
                              `$${(Number(customer.monthlySpend) || 0).toFixed(0)}` : 
                              '-'
                            }
                          </div>
                          {customer.projectedSpend !== undefined && customer.projectedSpend > 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Jun: ${(Number(customer.projectedSpend) || 0).toFixed(0)}
                              {customer.percentageChange !== undefined && customer.percentageChange !== 0 && (
                                <span className={`ml-1 ${customer.percentageChange > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                  {customer.percentageChange > 0 ? '+' : ''}{(Number(customer.percentageChange) || 0).toFixed(0)}%
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap w-1/12">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          customer.isActive ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                        }`}>
                          {customer.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right w-1/5">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleImpersonate(customer.id)}
                            disabled={impersonating === customer.id || !customer.isActive}
                            className={`inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded text-white ${
                              !customer.isActive 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={!customer.isActive ? "Cannot login to inactive customer" : "Login as customer"}
                          >
                            {impersonating === customer.id ? (
                              <>Loading...</>
                            ) : (
                              <>
                                <ArrowRightOnRectangleIcon className="h-3 w-3 mr-1" />
                                Login
                              </>
                            )}
                          </button>
                          {customer.isActive ? (
                            <button
                              onClick={() => handleCloseOpportunity(customer.id)}
                              disabled={closingCustomer === customer.id}
                              className="inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Close opportunity"
                            >
                              {closingCustomer === customer.id ? (
                                <>Loading...</>
                              ) : (
                                <>
                                  <XCircleIconOutline className="h-3 w-3 mr-1" />
                                  Close
                                </>
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReopenOpportunity(customer.id)}
                              disabled={closingCustomer === customer.id}
                              className="inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Re-open opportunity"
                            >
                              {closingCustomer === customer.id ? (
                                <>Loading...</>
                              ) : (
                                <>
                                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                                  Re-Open
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile list view */}
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 sm:hidden">
            {customers.length === 0 ? (
              <li className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                No customers found
              </li>
            ) : (
              customers.map((customer) => (
                <li key={customer.id}>
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {customer.companyName || customer.email}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {`${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'No name provided'}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        customer.isActive ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                      }`}>
                        {customer.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          {customer.hasActiveCredentials ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-500 dark:text-green-400 mr-1" />
                          ) : (
                            <XCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-1" />
                          )}
                          <span className="text-xs">API Key</span>
                        </div>
                        <div className="flex items-start">
                          <CurrencyDollarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-1" />
                          <div className="text-xs">
                            <span className="font-medium block">
                              {customer.monthlySpend !== undefined && customer.monthlySpend > 0 ? 
                                `$${(Number(customer.monthlySpend) || 0).toFixed(2)}` : 
                                '-'
                              }
                            </span>
                            {customer.projectedSpend !== undefined && customer.projectedSpend > 0 && (
                              <span className="text-gray-500 dark:text-gray-400 text-xs">
                                Jun: ${(Number(customer.projectedSpend) || 0).toFixed(0)}
                                {customer.percentageChange !== undefined && customer.percentageChange !== 0 && (
                                  <span className={customer.percentageChange > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                                    {' '}{customer.percentageChange > 0 ? '+' : ''}{customer.percentageChange.toFixed(0)}%
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleImpersonate(customer.id)}
                        disabled={impersonating === customer.id}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700"
                      >
                        Login to Environment
                      </button>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Pagination */}
        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-b-md">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">Show</span>
            <select
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700 dark:text-gray-300">Records per Page</span>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Showing {Math.min((currentPage - 1) * recordsPerPage + 1, stats.totalCustomers)} to{' '}
              {Math.min(currentPage * recordsPerPage, stats.totalCustomers)} of{' '}
              {stats.totalCustomers} results
            </span>

            <div className="flex items-center space-x-1">
              {/* First page */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                <svg className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              {/* Previous page */}
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous page"
              >
                <svg className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Page input */}
              <div className="flex items-center space-x-1">
                <span className="text-sm text-gray-700 dark:text-gray-300">Page</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value) || 1;
                    if (page >= 1 && page <= totalPages) {
                      setCurrentPage(page);
                    }
                  }}
                  className="w-16 px-2 py-1 text-center border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">of {totalPages}</span>
              </div>

              {/* Next page */}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next page"
              >
                <svg className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Last page */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                <svg className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Leads Section */}
        <div className="mt-8 bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Leads
            </h3>
          </div>
          
          {/* Leads Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-2/5">
                    Lead
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                    API Key
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                    Monthly Spend
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/12">
                    Status
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/5">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No leads found
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-2 whitespace-nowrap w-2/5">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {lead.companyName || lead.email}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">
                            {`${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'No name'}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap w-1/6">
                        <div className="flex items-center">
                          {lead.hasActiveCredentials ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Connected
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                              <XCircleIcon className="h-3 w-3 mr-1" />
                              Not Connected
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap w-1/6">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {lead.monthlySpend !== undefined && lead.monthlySpend > 0 ? 
                              `$${(Number(lead.monthlySpend) || 0).toFixed(0)}` : 
                              '-'
                            }
                          </div>
                          {lead.projectedSpend !== undefined && lead.projectedSpend > 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Jun: ${(Number(lead.projectedSpend) || 0).toFixed(0)}
                              {lead.percentageChange !== undefined && lead.percentageChange !== 0 && (
                                <span className={`ml-1 ${lead.percentageChange > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                  {lead.percentageChange > 0 ? '+' : ''}{(Number(lead.percentageChange) || 0).toFixed(0)}%
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap w-1/12">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          lead.isActive ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                        }`}>
                          {lead.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right w-1/5">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleImpersonate(lead.id)}
                            disabled={impersonating === lead.id || !lead.isActive}
                            className={`inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded text-white ${
                              !lead.isActive 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={!lead.isActive ? "Cannot login to inactive lead" : "Login as lead"}
                          >
                            {impersonating === lead.id ? (
                              <>Loading...</>
                            ) : (
                              <>
                                <ArrowRightOnRectangleIcon className="h-3 w-3 mr-1" />
                                Login
                              </>
                            )}
                          </button>
                          {lead.isActive ? (
                            <button
                              onClick={() => handleCloseOpportunity(lead.id)}
                              disabled={closingCustomer === lead.id}
                              className="inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Close lead"
                            >
                              {closingCustomer === lead.id ? (
                                <>Loading...</>
                              ) : (
                                <>
                                  <XCircleIconOutline className="h-3 w-3 mr-1" />
                                  Close
                                </>
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReopenOpportunity(lead.id)}
                              disabled={closingCustomer === lead.id}
                              className="inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Re-open lead"
                            >
                              {closingCustomer === lead.id ? (
                                <>Loading...</>
                              ) : (
                                <>
                                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                                  Re-Open
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* New Environment Modal */}
      {showNewEnvironmentModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={() => setShowNewEnvironmentModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleCreateEnvironment}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                      New Environment
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowNewEnvironmentModal(false)}
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  
                  {error && (
                    <div className="mb-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded relative">
                      <span className="block sm:inline">{error}</span>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="jobNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Job Number
                      </label>
                      <input
                        type="text"
                        name="jobNumber"
                        id="jobNumber"
                        required
                        value={newEnvironmentForm.jobNumber}
                        onChange={(e) => setNewEnvironmentForm({ ...newEnvironmentForm, jobNumber: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="e.g., OPP12345678"
                      />
                    </div>

                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        id="companyName"
                        required
                        value={newEnvironmentForm.companyName}
                        onChange={(e) => setNewEnvironmentForm({ ...newEnvironmentForm, companyName: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter company name"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isCreatingEnvironment}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreatingEnvironment ? 'Creating...' : 'Create Environment'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewEnvironmentModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="profile-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={() => setShowProfileModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUpdateProfile}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="profile-modal-title">
                      Edit Profile
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowProfileModal(false)}
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  
                  {profileError && (
                    <div className="mb-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded relative">
                      <span className="block sm:inline">{profileError}</span>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Company Name (read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={msp?.company || ''}
                        disabled
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Company name cannot be changed. Contact support if you need to update it.
                      </p>
                    </div>

                    {/* Contact Name */}
                    <div>
                      <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Contact Name
                      </label>
                      <input
                        type="text"
                        name="contactName"
                        id="contactName"
                        required
                        value={profileForm.contactName}
                        onChange={(e) => setProfileForm({ ...profileForm, contactName: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        required
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Phone Number (Optional)
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    {/* Logo Upload Dropzone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Company Logo
                      </label>
                      <div
                        className={`relative group cursor-pointer rounded-lg border-2 border-dashed ${
                          isDragging 
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        } transition-all duration-200`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(true);
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(false);
                          
                          const files = e.dataTransfer.files;
                          if (files && files[0]) {
                            const file = files[0];
                            if (file.type.startsWith('image/')) {
                              console.log('Dropped file:', file);
                              setProfileForm({ ...profileForm, logo: file });
                            } else {
                              setProfileError('Please drop an image file');
                            }
                          }
                        }}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,.svg';
                          input.onchange = (e) => {
                            const target = e.target as HTMLInputElement;
                            if (target.files && target.files[0]) {
                              console.log('Selected file:', target.files[0]);
                              setProfileForm({ ...profileForm, logo: target.files[0] });
                            }
                          };
                          input.click();
                        }}
                      >
                        <div className="p-8 text-center">
                          {(msp?.logoUrl || profileForm.logo) ? (
                            <div className="relative h-32 w-full">
                              {profileForm.logo ? (
                                <div className="h-full flex flex-col items-center justify-center">
                                  <img 
                                    src={URL.createObjectURL(profileForm.logo)}
                                    alt="New logo preview" 
                                    className="max-h-full w-auto max-w-full object-contain"
                                  />
                                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                    New logo: {profileForm.logo.name}
                                  </p>
                                </div>
                              ) : msp?.logoUrl ? (
                                <img 
                                  src={msp.logoUrl.startsWith('http') 
                                    ? msp.logoUrl 
                                    : `${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}${msp.logoUrl}`
                                  }
                                  alt="Company logo" 
                                  className="h-full w-full object-contain"
                                />
                              ) : null}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 rounded-lg transition-all duration-200 flex items-center justify-center">
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                  <PhotoIcon className="h-8 w-8 text-white" />
                                  <p className="mt-1 text-xs text-white">Click or drop to replace</p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                Drop logo here or click to select
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                PNG, JPG, GIF, SVG up to 5MB
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Password Change Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                        Change Password (Optional)
                      </h4>
                      
                      {/* New Password */}
                      <div className="mb-3">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          New Password
                        </label>
                        <input
                          type="password"
                          name="password"
                          id="password"
                          value={profileForm.password}
                          onChange={(e) => setProfileForm({ ...profileForm, password: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Leave blank to keep current password"
                        />
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          id="confirmPassword"
                          value={profileForm.confirmPassword}
                          onChange={(e) => setProfileForm({ ...profileForm, confirmPassword: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Confirm new password"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isUpdatingProfile}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowProfileModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};